/**
 * Read a user-picked `.json` file into parsed JSON, or a message the dialog can
 * show verbatim. Shared by every "import from JSON" surface (workflows,
 * procedures) so they reject the same inputs with the same words.
 */
export async function readJsonFile(
  file: File,
): Promise<{ ok: true; raw: unknown } | { ok: false; error: string }> {
  if (!file.name.endsWith('.json')) {
    return { ok: false, error: 'Please select a .json file.' };
  }

  let text: string;
  try {
    text = await file.text();
  } catch {
    return { ok: false, error: 'Failed to read the file.' };
  }

  return parseJsonText(text);
}

/** `JSON.parse` with the same failure message the file path uses. */
export function parseJsonText(
  text: string,
): { ok: true; raw: unknown } | { ok: false; error: string } {
  try {
    const raw: unknown = JSON.parse(text);
    return { ok: true, raw };
  } catch {
    return { ok: false, error: 'File is not valid JSON.' };
  }
}

/** First three `path: message` strings, plus a count of the rest. */
export function summarizeValidationErrors(errors: string[]): string {
  if (errors.length === 0) return 'Unknown validation error.';
  const extra = errors.length > 3 ? ` (+${errors.length - 3} more)` : '';
  return errors.slice(0, 3).join('; ') + extra;
}

/** Save JSON text as a downloaded file — the write side of the same import/export surfaces. */
export function downloadJsonText(fileName: string, json: string): void {
  const blob = new Blob([json], { type: 'application/json' });
  const url = URL.createObjectURL(blob);

  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
